import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiErrorMessage } from '../../shared/api/errors';
import { formatDateTime } from '../../shared/lib/format';
import { Button } from '../../shared/ui/Button';
import { Badge, Card, ErrorText, Spinner } from '../../shared/ui/primitives';
import { TextField } from '../../shared/ui/TextField';
import { createShareLink, listShareLinks, revokeShareLink } from './api';
import type { ShareLink } from './types';

const emailSchema = z.string().email('Введите корректный e-mail');

const statusLabel: Record<ShareLink['status'], { text: string; tone: 'accent' | 'neutral' }> = {
  active: { text: 'Действует', tone: 'accent' },
  expired: { text: 'Истекла', tone: 'neutral' },
  revoked: { text: 'Отозвана', tone: 'neutral' },
};

/** Отправка обследования на e-mail человеку без аккаунта: письмо со ссылкой,
 *  по которой доступно только скачивание архива (ссылка живёт ограниченное время). */
export function SharePanel({
  examinationId,
  defaultEmail,
  onClose,
}: {
  examinationId: string;
  defaultEmail: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState(defaultEmail);
  const [message, setMessage] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [sentTo, setSentTo] = useState('');

  const linksKey = ['examination', examinationId, 'share-links'];
  const links = useQuery({
    queryKey: linksKey,
    queryFn: () => listShareLinks(examinationId),
  });

  const send = useMutation({
    mutationFn: (body: { email: string; message: string }) => createShareLink(examinationId, body),
    onMutate: () => setSentTo(''),
    onSuccess: (link) => {
      setSentTo(link.email);
      setMessage('');
      queryClient.invalidateQueries({ queryKey: linksKey });
    },
  });

  const revoke = useMutation({
    mutationFn: (linkId: string) => revokeShareLink(examinationId, linkId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: linksKey }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = emailSchema.safeParse(email.trim());
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Введите корректный e-mail');
      return;
    }
    setFieldError('');
    send.mutate({ email: parsed.data, message: message.trim() });
  };

  return (
    <Card className="space-y-5 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink">Отправить на почту</h2>
          <p className="text-sm text-muted">
            Получатель (например, пациент или врач без доступа к кабинету) получит письмо со
            ссылкой. По ссылке можно только скачать архив этого обследования; ссылка действует
            ограниченное время, её можно отозвать.
          </p>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          Закрыть
        </Button>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <TextField
          label="E-mail получателя"
          type="email"
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldError}
          hint={defaultEmail && email === defaultEmail ? 'E-mail из карточки пациента' : undefined}
        />
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-ink/80">Сообщение (необязательно)</span>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={500}
            rows={2}
            className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-muted/70 focus-visible:border-accent"
          />
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" size="sm" disabled={send.isPending}>
            {send.isPending ? 'Отправляем…' : 'Отправить'}
          </Button>
          {sentTo && <span className="text-sm text-accent-600">Письмо отправлено на {sentTo}</span>}
          {send.isError && (
            <span className="text-sm text-danger">
              {apiErrorMessage(send.error, 'Не удалось отправить письмо')}
            </span>
          )}
        </div>
      </form>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-ink">Отправленные ссылки</h3>
        {links.isLoading && <Spinner />}
        {links.isError && (
          <ErrorText>{apiErrorMessage(links.error, 'Не удалось загрузить список')}</ErrorText>
        )}
        {links.data && links.data.length === 0 && (
          <p className="text-sm text-muted">Обследование ещё не отправляли.</p>
        )}
        {links.data && links.data.length > 0 && (
          <div className="divide-y divide-line rounded-xl border border-line">
            {links.data.map((l) => (
              <div
                key={l.id}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
              >
                <div className="min-w-0 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-medium text-ink">{l.email}</span>
                    <Badge tone={statusLabel[l.status].tone}>{statusLabel[l.status].text}</Badge>
                  </div>
                  <p className="text-xs text-muted">
                    {formatDateTime(l.createdAt)} · {l.createdByName}
                    {l.status === 'active' && <> · до {formatDateTime(l.expiresAt)}</>}
                    {' · '}
                    {l.downloadCount > 0
                      ? `скачано ${l.downloadCount} раз(а), последний — ${formatDateTime(l.lastDownloadedAt)}`
                      : 'не скачивали'}
                  </p>
                </div>
                {l.status === 'active' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    disabled={revoke.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Отозвать ссылку для ${l.email}? Она сразу перестанет работать.`,
                        )
                      ) {
                        revoke.mutate(l.id);
                      }
                    }}
                  >
                    Отозвать
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        {revoke.isError && (
          <ErrorText>{apiErrorMessage(revoke.error, 'Не удалось отозвать ссылку')}</ErrorText>
        )}
      </div>
    </Card>
  );
}
